import React from 'react';
import PropTypes from 'prop-types';
import block from 'bem-cn-lite';
import _ from 'lodash';
import {HTML} from '@doc-tools/components';

import ToggleArrow from 'components/ToggleArrow/ToggleArrow';
import {KeyCodes} from '../../constants';

import './Tabs.scss';

const b = block('Tabs');

class Tab extends React.Component {
    static propTypes = {
        title: PropTypes.string.isRequired,
        children: PropTypes.node.isRequired,
    };

    render() {
        return null;
    }
}

// eslint-disable-next-line valid-jsdoc
/** @deprecated */
export default class Tabs extends React.Component {
    static propTypes = {
        children: PropTypes.arrayOf(PropTypes.element).isRequired,
        theme: PropTypes.oneOf(['default', 'full']),
        activeTabIndex: PropTypes.number,
        onTabClick: PropTypes.func,
        mobileAlways: PropTypes.bool,
    };

    static defaultProps = {
        theme: 'default',
    };

    state = {
        activeTab: this.props.activeTabIndex >= 0 ? this.props.activeTabIndex : 0,
        listOpen: false,
    };

    componentDidMount() {
        // We couldn't generate these ids in constructor because of SSR,
        // rendered content must match on client and server.
        this.tabIds = React.Children.map(this.props.children, () => _.uniqueId('tab$'));
        this.panelIds = React.Children.map(this.props.children, () => _.uniqueId('panel$'));
        this.forceUpdate();
    }

    componentDidUpdate(prevPops) {
        if (prevPops.activeTabIndex !== this.props.activeTabIndex) {
            this.setState({activeTab: this.props.activeTabIndex});
        }
    }

    componentWillUnmount() {
        this.detachClickListener();
    }

    static Tab = Tab;

    saveTabRef = (index, ref) => {
        this.tabRefs = this.tabRefs || [];
        this.tabRefs[index] = ref;
    };

    attachClickListener() {
        document.addEventListener('click', this.handleGlobalClick);
    }

    detachClickListener() {
        document.removeEventListener('click', this.handleGlobalClick);
    }

    getTabId(index) {
        return this.tabIds && this.tabIds[index];
    }

    getPanelId(index) {
        return this.panelIds && this.panelIds[index];
    }

    getActiveTabText() {
        const {children} = this.props;
        const {activeTab} = this.state;

        return React.Children.toArray(children)[activeTab].props.title;
    }

    focusActiveTab = () => {
        const {activeTab} = this.state;

        this.tabRefs[activeTab].focus();
    };

    selectNextTab = () => {
        const {children} = this.props;
        const count = React.Children.count(children);

        this.setState((prevState) => {
            let nextActiveTab = prevState.activeTab + 1;

            if (nextActiveTab > count - 1) {
                nextActiveTab = 0;
            }

            return {
                activeTab: nextActiveTab,
            };
        }, this.focusActiveTab);
    };

    selectPrevTab = () => {
        const {children} = this.props;
        const count = React.Children.count(children);

        this.setState((prevState) => {
            let prevActiveTab = prevState.activeTab - 1;

            if (prevActiveTab < 0) {
                prevActiveTab = count - 1;
            }

            return {
                activeTab: prevActiveTab,
            };
        }, this.focusActiveTab);
    };

    toggleList = () => {
        const {listOpen} = this.state;

        return listOpen ? this.closeList() : this.openList();
    };

    openList = () => {
        this.setState({listOpen: true});
        this.attachClickListener();
    };

    closeList = () => {
        this.setState({listOpen: false});
        this.detachClickListener();
    };

    handleGlobalClick = (event) => {
        if (!this.tabList.contains(event.target) && !this.tabListToggle.contains(event.target)) {
            this.closeList();
        }
    };

    handleTabClick(index) {
        this.setState({activeTab: index, listOpen: false});

        if (this.props.onTabClick) {
            this.props.onTabClick(index);
        }
    }

    handleTabKeyDown = (event) => {
        switch (event.keyCode) {
            case KeyCodes.ARROW_LEFT:
                this.selectPrevTab();
                event.preventDefault();
                break;
            case KeyCodes.ARROW_RIGHT:
                this.selectNextTab();
                event.preventDefault();
                break;
        }
    };

    renderTabs() {
        const {children} = this.props;
        const {activeTab, listOpen} = this.state;

        const tabs = React.Children.map(children, (element, index) => {
            const {title} = element.props;
            const active = index === activeTab;
            const id = this.getTabId(index);
            const panelId = this.getPanelId(index);

            return (
                <li
                    ref={this.saveTabRef.bind(this, index)}
                    key={id || index}
                    tabIndex={active ? 0 : -1}
                    role="tab"
                    aria-selected={active}
                    aria-controls={panelId}
                    id={id}
                    className={b('tab', {active})}
                    onClick={this.handleTabClick.bind(this, index)}
                    onKeyDown={this.handleTabKeyDown}
                >
                    <HTML>{title}</HTML>
                </li>
            );
        });

        return (
            <div className={b('tab-list-wrapper')}>
                <button
                    ref={(ref) => {
                        this.tabListToggle = ref;
                    }}
                    type="button"
                    className={b('tab-list-toggle')}
                    onClick={this.toggleList}
                >
                    <HTML>{this.getActiveTabText()}</HTML>
                    <ToggleArrow open={true} />
                </button>
                <ul
                    ref={(ref) => {
                        this.tabList = ref;
                    }}
                    role="tablist"
                    className={b('tab-list', {open: listOpen})}
                >
                    {tabs}
                </ul>
            </div>
        );
    }

    renderPanels() {
        const {children} = this.props;
        const {activeTab} = this.state;

        return React.Children.map(children, (element, index) => {
            const {children: localChildren} = element.props;
            const active = index === activeTab;
            const id = this.getPanelId(index);
            const tabId = this.getTabId(index);

            return (
                <div
                    key={id || index}
                    role="tabpanel"
                    aria-labelledby={tabId}
                    id={id}
                    className={b('panel')}
                    style={{display: active ? 'block' : 'none'}}
                >
                    {localChildren}
                </div>
            );
        });
    }

    render() {
        const {theme, mobileAlways} = this.props;
        return (
            <div className={b({theme, 'mobile-always': mobileAlways})}>
                {this.renderTabs()}
                {this.renderPanels()}
            </div>
        );
    }
}
